import React from 'react';
import { FileCheck, ChevronDown, ChevronRight } from 'lucide-react';
import { utcIso8601 } from '../utils/formatters';

export interface EvidencePassportShape {
  as_of: string;
  input_set_hash: string;
  snapshot_sha256: string;
  typed_metric_registry_sha256: string;
  suppression_ledger_sha256: string;
  policy_version: string;
  canonical_status_sha256: string;
}

interface Props {
  passport?: EvidencePassportShape | null;
  className?: string;
}

// The 64-zero placeholder emitted by the backend for components whose infrastructure has not yet
// landed (typed_metric_registry, suppression_ledger). Kept in sync with the Go constant
// reports.HashPlaceholder.
const HASH_PLACEHOLDER = 'sha256:0000000000000000000000000000000000000000000000000000000000000000';

const shortHash = (h: string): string => {
  if (!h || !h.startsWith('sha256:')) return h ?? '';
  const body = h.slice('sha256:'.length);
  if (body.length <= 12) return h;
  return `sha256:${body.slice(0, 6)}…${body.slice(-4)}`;
};

/**
 * Reusable footer for every number-bearing surface (contract-pack §5). Shows the collapsed
 * summary chip row (as-of · canonical_status_sha256 · policy_version) with a click-to-expand
 * reveal of the full seven-field passport. Components whose upstream infrastructure has not
 * yet landed render as "TYPED_REGISTRY (pending)" / "SUPPRESSION_LEDGER (pending)" — a
 * declared absence, never hidden.
 */
const EvidencePassportFooter: React.FC<Props> = ({ passport, className }) => {
  const [open, setOpen] = React.useState(false);
  if (!passport || !passport.canonical_status_sha256) return null;

  const registryPending = passport.typed_metric_registry_sha256 === HASH_PLACEHOLDER;
  const ledgerPending = passport.suppression_ledger_sha256 === HASH_PLACEHOLDER;

  return (
    <div
      className={`mt-3 border-t border-[color:rgb(var(--c-border-rgb)/0.6)] pt-2 text-[10px] font-mono text-cmd-textDim ${className ?? ''}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex flex-wrap items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-[color:rgb(var(--c-ink-rgb)/0.05)]"
        title="Click to expand the full evidence passport (contract-pack §5)."
      >
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        <FileCheck size={11} />
        <span>Evidence passport</span>
        <span>·</span>
        <span>As of {utcIso8601(passport.as_of)}</span>
        <span>·</span>
        <span>Canon {shortHash(passport.canonical_status_sha256)}</span>
        <span>·</span>
        <span>Policy {passport.policy_version}</span>
      </button>
      {open && (
        <div className="mt-2 grid gap-1 rounded border border-[color:rgb(var(--c-ink-rgb)/0.05)] bg-cmd-panel/60 p-2">
          <Row label="as_of" value={utcIso8601(passport.as_of)} />
          <Row label="input_set_hash" value={passport.input_set_hash} full />
          <Row label="snapshot_sha256" value={passport.snapshot_sha256} full />
          <Row
            label="typed_metric_registry_sha256"
            value={registryPending ? 'pending (infra not yet landed)' : passport.typed_metric_registry_sha256}
            pending={registryPending}
            full={!registryPending}
          />
          <Row
            label="suppression_ledger_sha256"
            value={ledgerPending ? 'pending (infra not yet landed)' : passport.suppression_ledger_sha256}
            pending={ledgerPending}
            full={!ledgerPending}
          />
          <Row label="policy_version" value={passport.policy_version} />
          <Row label="canonical_status_sha256" value={passport.canonical_status_sha256} full highlight />
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value: string; full?: boolean; pending?: boolean; highlight?: boolean }> = ({
  label,
  value,
  full,
  pending,
  highlight,
}) => (
  <div className="grid grid-cols-1 items-baseline gap-x-2 gap-y-0.5 sm:grid-cols-[220px_1fr]">
    <span className="text-cmd-textDim">{label}</span>
    <span
      className={`break-all ${pending ? 'text-amber-400' : highlight ? 'text-emerald-300' : 'text-cmd-text'}`}
    >
      {full ? value : value}
    </span>
  </div>
);

export default EvidencePassportFooter;
